// @flow
import { createSelector } from 'reselect';
import type { State, CarEntity, Car, SessionConfig, DebugConfig } from '../types';
import { getActivityIndex } from '../utils/targetUtils';
import { sessionConfigSelector, debugConfigSelector } from './configSelectors';
import { makeVehicleEntitySelector } from './targetSelectors';

const carStateSelector = (state: State) => state.cars;

export const carListSelector = (state: State) => {
    const carState = carStateSelector(state);
    return carState.list;
};

export const carEntitiesSelector = (state: State) => {
    const carState = carStateSelector(state);
    return carState.entities;
};

export const makeCarEntitySelector = (icao: string) => (state: State) => {
    const entities = carEntitiesSelector(state);
    return entities[icao];
};

export const getDataSource = (entity: CarEntity, sessionConfig: SessionConfig, debugConfig: DebugConfig) => {
    if(!entity) {
        return entity;
    }

    if(debugConfig && debugConfig.discardSystemDataSource && entity.device) {
        return 'device';
    }

    if(typeof entity.system !== 'undefined') {
        if(typeof entity.device === 'undefined' || typeof entity.device[0] === 'undefined' || typeof entity.system[0] === 'undefined') {
            return 'system';
        }

        const systemAge = Date.now() - new Date(entity.system[0].TimeOfTrackInformation);
        const deviceAge = Date.now() - new Date(entity.device[0].TimeOfTrackInformation);

        if(systemAge > sessionConfig.fallbackTimeout && deviceAge < systemAge) {
            return 'device';
        }
        else {
            return 'system';
        }
    }
    else {
        return 'device';
    }
};
export const makeDataSourceSelector = (icao: string) => (state: State) => {
    const sessionConfig = sessionConfigSelector(state);
    const debugConfig = debugConfigSelector(state);
    const entity = makeCarEntitySelector(icao)(state);

    return getDataSource(entity, sessionConfig, debugConfig);
};

export const makeCarEntitySystemSelector = (icao: string) => (state: State) => {
    const entity = makeCarEntitySelector(icao)(state);
    return entity && entity.system;
};

export const makeCarEntityDeviceSelector = (icao: string) => (state: State) => {
    const entity = makeCarEntitySelector(icao)(state);
    return entity && entity.device;
};

export const makeCarEntityAutoSelector = (icao: string) => (state: State) => {
    const dataSource = makeDataSourceSelector(icao)(state);
    const entity = makeCarEntitySelector(icao)(state);

    return entity && dataSource && entity[dataSource];
};

export const activeCarListSelector = createSelector(
    [carListSelector, carEntitiesSelector, sessionConfigSelector, debugConfigSelector, (state, props) => props.now],
    (list, entities, sessionConfig, debugConfig, now) => {
        const activityMap = {};
        return list
            .filter(icao => {
                const entity = entities[icao];
                const dataSource = getDataSource(entity, sessionConfig, debugConfig);
                const target = entity && dataSource && entity[dataSource] && entity[dataSource][0];

                if(!target) {
                    return false;
                }

                const activityIndex = getActivityIndex(target.TimeOfTrackInformation, sessionConfig, now);
                const active = activityIndex > 0;

                if(active) {
                    activityMap[icao] = activityIndex;
                }
                else {
                    delete activityMap[icao];
                }

                return active;
            })
            .sort((a, b) => {
                if(activityMap[a] > activityMap[b]) {
                    return -1;
                }
                else if(activityMap[a] < activityMap[b]) {
                    return 1;
                }
                else {
                    return 0;
                }
            });
    }
);

export const makeCarSessionStartSelector = (icao: string) => (state: State) => {
    const entity = makeCarEntitySelector(icao)(state);
    return entity && entity.sessionStart;
};

export const makeCarSessionActiveSelector = (icao: string) => (state: State) => {
    const sessionConfig = sessionConfigSelector(state);
    const car = makeCarEntityAutoSelector(icao)(state);

    if(!car) {
        return false;
    }

    const lastUpdate = car.TimeOfTrackInformation;
    const activityDuration = typeof lastUpdate !== 'undefined' && Date.now() - new Date(lastUpdate);

    return typeof activityDuration !== 'undefined' && activityDuration < sessionConfig.disconnected;
};

export const carCountSelector = (state: State) => {
    const carState = carStateSelector(state);
    return carState.list.length;
};

export const callsignSelector = (icao: string, carBuffer?: $ReadOnlyArray<Car>) => (state: State) => {
    const car = carBuffer && carBuffer[0];

    if(car && car.TargetIdentification) {
        return car.TargetIdentification;
    }
    else {
        const vehicleEntity = makeVehicleEntitySelector(icao)(state);
        return vehicleEntity && vehicleEntity.callsign;
    }
};